import type { SessionUser } from '@arquivo-morto/contracts';
import {
  Body1Strong,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ChevronRightRegular, HomeRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router';
import {
  type ModuleDef,
  type PageDef,
  pagePath,
} from '#src/app/navigation/module';
import { MODULES } from '#src/app/navigation/modules';
import { visibleModules } from '#src/app/navigation/navigation';
import { useAccess, useSession } from '#src/auth/session';
import { Card, EmptyState, IconTile, PageShell } from '#src/shared/ui';

const useStyles = makeStyles({
  columns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)',
    alignItems: 'start',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 1023px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    listStyleType: 'none',
    margin: 0,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    width: '100%',
    padding: tokens.spacingHorizontalM,
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    borderRadius: tokens.borderRadius2XLarge,
    color: 'inherit',
    fontFamily: 'inherit',
    textAlign: 'start',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      border: `1px solid ${tokens.colorBrandStroke2}`,
    },
    ':focus-visible': {
      outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
    },
  },
  rowText: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  subtle: { color: tokens.colorNeutralForeground3 },
  arrow: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
});

interface Destination {
  readonly module: ModuleDef;
  readonly page: PageDef;
}

function DestinationRow({ module, page }: Destination) {
  const styles = useStyles();
  const navigate = useNavigate();
  const Icon = page.icon;
  return (
    <li>
      <button
        type="button"
        className={styles.row}
        onClick={() => navigate(pagePath(module, page))}
      >
        <IconTile>
          <Icon />
        </IconTile>
        <span className={styles.rowText}>
          <Body1Strong>{page.title}</Body1Strong>
          <Caption1 className={styles.subtle}>{page.description}</Caption1>
        </span>
        <ChevronRightRegular className={styles.arrow} />
      </button>
    </li>
  );
}

function DestinationList({ items }: { items: readonly Destination[] }) {
  const styles = useStyles();
  return (
    <ul className={styles.list}>
      {items.map((item) => (
        <DestinationRow key={pagePath(item.module, item.page)} {...item} />
      ))}
    </ul>
  );
}

function greeting({ name, roles }: SessionUser): {
  title: string;
  description: string;
} {
  const firstName = name.split(' ')[0] ?? name;
  const description =
    roles.length === 0
      ? 'Você ainda não tem um perfil: por enquanto, vê só o que é aberto a todos. Peça acesso a um administrador.'
      : `Você está conectado como ${roles.join(', ')}. Escolha por onde começar.`;
  return { title: `Olá, ${firstName}`, description };
}

/** Início: atalhos para o que a pessoa pode abrir, módulos à esquerda. */
export function HomePage() {
  const styles = useStyles();
  const { user } = useSession();
  const visible = visibleModules(MODULES, useAccess());
  const destinations = (placement: ModuleDef['placement']) =>
    visible
      .filter(({ module }) => module.placement === placement)
      .flatMap(({ module, pages }) => pages.map((page) => ({ module, page })));
  const main = destinations('main');
  const footer = destinations('footer');

  return (
    <PageShell icon={<HomeRegular />} {...greeting(user)}>
      <div className={styles.columns}>
        <Card title="Seus módulos">
          {main.length > 0 ? (
            <DestinationList items={main} />
          ) : (
            <EmptyState
              title="Nenhum módulo disponível para o seu perfil"
              description="Se você precisa de acesso, peça a liberação a um administrador."
            />
          )}
        </Card>
        {footer.length > 0 && (
          <Card title="Acesso rápido">
            <DestinationList items={footer} />
          </Card>
        )}
      </div>
    </PageShell>
  );
}
